import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@shared/components/ui/button";
import ExperienceList from "@features/booking/components/ExperienceList";
import { BookingBreadcrumbs } from "@features/booking/components/BookingBreadcrumbs";
import { BookingPageLayout } from "@features/booking/layouts";
import { useBooking } from "@features/booking/state";
import { useBookingFlow } from "@features/booking/flow";
import { useRestaurant } from "@core/tenant/TenantProvider";
import { useLanguage } from "@core/i18n";
import { EarlyAccessBanner } from "@features/booking/components/EarlyAccessBanner";
import { filterSlotMenusByArea } from "@features/booking/utils/areaUtils";
import { PUBLIC_SEARCH_MODE } from "@shared/types/restaurant";
import type { RestaurantMenu } from "@shared/types/restaurant";
import type { TimeSlotMenu } from "@features/booking/types/timeSlots";
import { findAlternativeTimesForMenu, resolveSwitchTimeSelection } from '@features/booking/utils/experienceAvailability';

/**
 * Enrich slot menus with translated description, price, and details
 * from RESTAURANT.menus[lang] — replicates legacy step1.js setMenus().
 *
 * Price is the exception: the slot menu comes from search-all-hours for the
 * booking date, so it carries the seasonal price when one is active, while the
 * restaurant payload is undated (base price). A slot that carries the numeric
 * fields keeps its own price, including when that price is nothing: the slot's
 * `price` is the same derived string the confirmation email, the Partner API and
 * the reservation snapshot print for that date (AvailabilityService sets it from
 * `priceFor($date)->display`, never omitting it), so falling back to the undated
 * base price would show a number the reservation will not record. A slot without
 * the numeric fields (older backend) takes the restaurant payload as before.
 */
function enrichMenus(
  slotMenus: TimeSlotMenu[],
  restaurantMenus: Record<string, RestaurantMenu[]>,
  lang: string,
): TimeSlotMenu[] {
  const translated = restaurantMenus[lang] ?? restaurantMenus['en'] ?? [];
  if (translated.length === 0) return slotMenus;

  const enriched = slotMenus.map((menu) => {
    const found = translated.find((t) => t.id === menu.id);
    if (!found) return menu;
    const slotIsPriceAuthority = menu.priceAmount !== undefined;
    return {
      ...menu,
      name: found.name || menu.name,
      description: found.description || undefined,
      price: slotIsPriceAuthority ? menu.price : found.price || undefined,
      priceAmount: slotIsPriceAuthority ? menu.priceAmount : found.priceAmount,
      priceCurrency: slotIsPriceAuthority ? menu.priceCurrency : found.priceCurrency,
      details: found.details || undefined,
      preferredAccess: found.preferredAccess,
      orderPriority: found.orderPriority ?? 0,
    };
  });

  // Sort: event menus first, then by orderPriority (matches legacy)
  return enriched.sort((a, b) => {
    if (a.eventMenu && !b.eventMenu) return -1;
    if (!a.eventMenu && b.eventMenu) return 1;
    return (a.orderPriority ?? 0) - (b.orderPriority ?? 0);
  });
}

const ExperienceSelector = () => {
  const { t } = useTranslation('booking');
  const booking = useBooking();
  const { goToNextFromStep } = useBookingFlow();
  const restaurant = useRestaurant();
  const { currentLanguage } = useLanguage();

  // When a single menu is pre-selected (guarantee/price case), expand it by default
  const [expandedMenuId, setExpandedMenuId] = useState<string | null>(
    () => booking.selectedExperience?.id ?? null
  );

  // Get menus from the selected time slot
  const selectedSlot = booking.selectedTime && booking.timeSlots
    ? booking.timeSlots.find((s) => s.hour === booking.selectedTime)
    : null;

  const slotMenus = selectedSlot?.menus ?? [];

  const isAreaMode = restaurant.publicSearchMode === PUBLIC_SEARCH_MODE.AREA;
  const areaFilteredMenus = useMemo(() => {
    if (!isAreaMode || !booking.selectedArea) return slotMenus;
    return filterSlotMenusByArea(slotMenus, booking.selectedArea);
  }, [isAreaMode, booking.selectedArea, slotMenus]);

  // Enrich with translated description/price/details from restaurant config
  const enrichedMenus = useMemo(
    () => enrichMenus(areaFilteredMenus, restaurant.menus ?? {}, currentLanguage),
    [areaFilteredMenus, restaurant.menus, currentLanguage],
  );

  const areaCtx = { isAreaMode, selectedArea: booking.selectedArea };

  const alternativeTimes = useMemo(() => {
    const map: Record<number, string[]> = {};
    for (const menu of enrichedMenus) {
      if (!menu.disabled || menu.preferredAccess) continue;
      const alts = findAlternativeTimesForMenu(booking.timeSlots, menu.id, areaCtx);
      if (alts.length) map[menu.id] = alts;
    }
    return map;
    // areaCtx is derived from isAreaMode + booking.selectedArea, both already in deps above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrichedMenus, booking.timeSlots, isAreaMode, booking.selectedArea]);

  // Auto-select menu when rescheduling (if the original menu is available)
  const rescheduleMenuId = booking.rescheduleMenuId;
  if (rescheduleMenuId && !booking.selectedExperience && enrichedMenus.length > 0) {
    const match = enrichedMenus.find((m) => m.id === rescheduleMenuId);
    if (match) {
      booking.setSelectedExperience({ id: String(match.id), title: match.name });
      if (!isAreaMode && match.area) {
        booking.setSelectedArea({ id: match.area, name: match.area });
      }
    }
  }

  const handleSelect = (menu: TimeSlotMenu) => {
    booking.setSelectedExperience({
      id: String(menu.id),
      title: menu.name,
    }, { byGuest: true });
    if (!isAreaMode && menu.area) {
      booking.setSelectedArea({
        id: menu.area,
        name: menu.area,
      });
    }
  };

  const handleSwitchTime = (menuId: number, newTime: string) => {
    const sel = resolveSwitchTimeSelection(booking.timeSlots, menuId, newTime, areaCtx);
    if (!sel) return;
    booking.setSelectedTime(sel.time);
    booking.setSelectedExperience(sel.experience, { byGuest: true });
    // sel.area is only set in menu mode (derived from the menu, never a guest
    // pick), so it is an automatic write.
    if (sel.area) booking.setSelectedArea(sel.area);
  };

  const handleToggleExpand = (menuId: string) => {
    setExpandedMenuId((prev) => (prev === menuId ? null : menuId));
    // Expanding a menu also selects it
    const menu = enrichedMenus.find((m) => String(m.id) === menuId);
    if (menu) handleSelect(menu);
  };

  const handleContinue = () => {
    if (booking.selectedExperience) {
      // Resolve from EXPERIENCE's canonical position, not the current path:
      // the alternative-time switch above can land on a slot whose single
      // plain menu makes this very step inactive, and a path-based goToNext()
      // would silently no-op (dead Continue). See resolveNextStepAfter.
      goToNextFromStep('EXPERIENCE');
    }
  };

  return (
    <BookingPageLayout
      title={t('STRING_PAYMENT_STEP_1')}
      subtitle={t('EXPERIENCE_SUBTITLE')}
      breadcrumbs={<BookingBreadcrumbs />}
    >
      <EarlyAccessBanner />
      <ExperienceList
        menus={enrichedMenus}
        selectedMenuId={booking.selectedExperience?.id ?? null}
        expandedMenuId={expandedMenuId}
        onToggleExpand={handleToggleExpand}
        selectedTime={booking.selectedTime}
        alternativeTimes={alternativeTimes}
        onSwitchTime={handleSwitchTime}
      />

      <div className="w-full px-6 mt-8">
        <Button
          data-testid="continue-btn"
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleContinue}
          disabled={!booking.selectedExperience}
        >
          {t('NEW_MODAL_CONTINUE')}
        </Button>
      </div>
    </BookingPageLayout>
  );
};

export default ExperienceSelector;
